import type { CostDateRange } from '../utils/aiCostPeriod';
/**
 * React-Admin Data Provider
 * Maps react-admin data operations to ReKindle admin API
 */

import { DataProvider, GetListParams, GetOneParams, UpdateParams } from 'react-admin';
import { apiClient } from './apiClient';
import type { AdminPlanGrant, AdminSubscription } from '@rekindle/shared/types/adminSubscription';
import { logger } from '../utils/logger';
import type { AICostCoverage, AICostCallFilters, AICostCallsResult } from '../utils/aiCostPresentation';

/**
 * Build query string from react-admin params
 */
function buildQueryString(params: GetListParams): string {
  const page = params.pagination?.page ?? 1;
  const perPage = params.pagination?.perPage ?? 25;
  const field = params.sort?.field ?? 'id';
  const order = params.sort?.order ?? 'ASC';

  const queryParams = new URLSearchParams({
    _start: String((page - 1) * perPage),
    _end: String(page * perPage),
    _sort: field,
    _order: order,
  });

  // Add filters
  if (params.filter && Object.keys(params.filter).length > 0) {
    queryParams.set('filter', JSON.stringify(params.filter));
  }

  return queryParams.toString();
}

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Data Provider implementation
 * Note: Using 'any' types as required by react-admin's DataProvider interface
 */
export const dataProvider: DataProvider = {
  /**
   * GET /admin/{resource}
   * List resources with pagination
   */
  getList: async (resource, params: GetListParams) => {
    const query = buildQueryString(params);
    const response = await apiClient.get<{ data: any[]; total: number }>(
      `/admin/${resource}?${query}`
    );

    return {
      data: response.data,
      total: response.total,
    };
  },

  /**
   * GET /admin/{resource}/{id}
   * Get single resource
   */
  getOne: async (resource, params: GetOneParams) => {
    const response = await apiClient.get<any>(`/admin/${resource}/${params.id}`);
    return { data: response };
  },

  /**
   * GET /admin/{resource}/{id} for each requested reference
   * Resolve exact records rather than searching a paginated list.
   */
  getMany: async (resource, params) => {
    // Reference selectors need these exact records, including those outside page one.
    // Do not replace a failed lookup with a partial/default list.
    const data = await Promise.all(params.ids.map(id =>
      apiClient.get<any>(`/admin/${resource}/${encodeURIComponent(String(id))}`)));
    return { data };
  },

  /**
   * GET /admin/{resource}?{target}={id}
   * Get resources related to another resource
   */
  getManyReference: async (resource, params) => {
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;

    const queryParams = new URLSearchParams({
      _start: String((page - 1) * perPage),
      _end: String(page * perPage),
      _sort: field,
      _order: order,
      [params.target]: String(params.id),
    });

    if (Object.keys(params.filter).length > 0) {
      queryParams.set('filter', JSON.stringify(params.filter));
    }

    const response = await apiClient.get<{ data: any[]; total: number }>(
      `/admin/${resource}?${queryParams.toString()}`
    );

    return {
      data: response.data,
      total: response.total,
    };
  },

  /**
   * PUT /admin/{resource}/{id}
   * Update a resource
   */
  update: async (resource, params: UpdateParams) => {
    const response = await apiClient.put<any>(
      `/admin/${resource}/${params.id}`,
      params.data
    );
    return { data: response };
  },

  /**
   * PUT /admin/{resource}
   * Update multiple resources
   */
  updateMany: async (resource, params) => {
    const results = await Promise.allSettled(
      params.ids.map((id) =>
        apiClient.put<{ id: string | number }>(`/admin/${resource}/${id}`, params.data)
      )
    );

    const successfulIds: (string | number)[] = [];
    const errors: string[] = [];

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        successfulIds.push(result.value.id);
      } else {
        errors.push(`Failed to update ${params.ids[index]}: ${result.reason}`);
      }
    });

    if (errors.length > 0 && successfulIds.length === 0) {
      throw new Error(`All updates failed: ${errors.join('; ')}`);
    }

    if (errors.length > 0) {
      // Partial success - log warnings but return successful ones
      logger.warn(`Some updates failed: ${errors.join('; ')}`);
    }

    return { data: successfulIds };
  },

  /**
   * POST /admin/{resource}
   * Create a resource
   */
  create: async (resource, params) => {
    const response = await apiClient.post<any>(`/admin/${resource}`, params.data);
    return { data: response };
  },

  /**
   * DELETE /admin/{resource}/{id}
   * Delete a resource
   */
  delete: async (resource, params) => {
    const response = await apiClient.delete<any>(`/admin/${resource}/${params.id}`);
    return { data: response };
  },

  /**
   * DELETE /admin/{resource}?id=1&id=2
   * Delete multiple resources
   */
  deleteMany: async (resource, params) => {
    const results = await Promise.allSettled(
      params.ids.map((id) => apiClient.delete(`/admin/${resource}/${id}`))
    );

    const successfulIds: (string | number)[] = [];
    const errors: string[] = [];

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        successfulIds.push(params.ids[index]);
      } else {
        errors.push(`Failed to delete ${params.ids[index]}: ${result.reason}`);
      }
    });

    if (errors.length > 0 && successfulIds.length === 0) {
      throw new Error(`All deletions failed: ${errors.join('; ')}`);
    }

    if (errors.length > 0) {
      // Partial success - log warnings but return successful ones
      logger.warn(`Some deletions failed: ${errors.join('; ')}`);
    }

    return { data: successfulIds };
  },
};

/**
 * Custom methods for admin-specific operations
 */
export const adminApi = {
  getSharedAllowance: (userId: string) => apiClient.get<{
    pairId: string; bonus: number; version: number;
    usage: import('@rekindle/shared/chat/conversations').ConversationUsage;
  } | null>(`/admin/chat/users/${userId}/shared`),
  adjustSharedAllowance: (userId: string, input: { pairId: string; version: number; amount: number; requestId: string }) =>
    apiClient.post<{ totalBonus: number }>(`/admin/chat/users/${userId}/shared/bonus`, input),
  /**
   * Grant bonus usage to a user
   */
  grantBonusUsage: async (userId: string, feature: string, amount: number, requestKey: string) => {
    return apiClient.post<{
      success: boolean;
      message: string;
      userId: string;
      feature: string;
      bonusAmount: number;
      totalBonus: number;
    }>(`/admin/users/${userId}/bonus-usage`, { feature, amount, requestKey });
  },

  /**
   * Set bonus usage to a specific value (can decrease or remove)
   */
  updateBonusUsage: async (userId: string, feature: string, amount: number, requestKey: string, expectedTotal: number) => {
    return apiClient.put<{
      success: boolean;
      message: string;
      userId: string;
      feature: string;
      bonusAmount: number;
      totalBonus: number;
    }>(`/admin/users/${userId}/bonus-usage`, { feature, amount, requestKey, expectedTotal });
  },

  /**
   * Get user usage stats with bonus breakdown
   */
  getUserUsage: async (userId: string) => {
    return apiClient.get<{
      userId: string;
      email: string;
      name: string | null;
      planTier: string;
      effectiveTier: string;
      isSharedSubscription: boolean;
      partnerInfo: {
        partnerId: string;
        partnerEmail: string;
        partnerName: string | null;
        partnerTier: string;
      } | null;
      features: Array<{
        feature: string;
        displayName: string;
        used: number;
        planLimit: number;
        bonus: number;
        purchasedSlots?: number;
        effectiveLimit: number;
        remaining: number;
        unlimited: boolean;
        resetPeriod: string;
      }>;
    }>(`/admin/users/${userId}/usage`);
  },

  /**
   * Update user plan tier
   */
  updateUserPlan: async (userId: string, grant: AdminPlanGrant) => {
    return apiClient.post<{ success: boolean; newTier: string; subscription: AdminSubscription }>(
      `/admin/users/${userId}/plan`,
      grant
    );
  },

  /**
   * Bulk upgrade users
   */
  bulkUpgradeUsers: async (userIds: string[], grant: AdminPlanGrant) => {
    return apiClient.post<{ success: boolean; upgradedCount: number }>(
      '/admin/users/bulk-upgrade',
      { userIds, ...grant }
    );
  },

  /**
   * Broadcast notification
   */
  broadcastNotification: async (
    target: 'all' | string[],
    translations: Record<string, { title: string; message: string }>,
    options?: { icon?: string; route?: string }
  ) => {
    return apiClient.post<{ success: boolean; sentCount: number }>(
      '/admin/notifications/broadcast',
      { target, translations, ...options }
    );
  },

  /**
   * Auto-translate notification content to all supported languages
   */
  translateNotification: async (
    title: string,
    message: string,
    targetLanguages?: string[]
  ) => {
    return apiClient.post<{
      success: boolean;
      translations: Record<string, { title: string; message: string }>;
    }>('/admin/notifications/translate', { title, message, targetLanguages });
  },

  /**
   * Get dashboard stats
   */
  getStats: async () => {
    return apiClient.get<{
      users: {
        total: number;
        withProfile: number;
        withPartner: number;
        newThisWeek: number;
        newThisMonth: number;
      };
      subscriptions: Record<string, number>;
      activity: {
        activeToday: number;
        activeThisWeek: number;
        activeThisMonth: number;
      };
    }>('/admin/stats');
  },










  /**
   * Get paginated list of users with AI cost summaries
   */
  getAICostUsers: async (
    start: number,
    end: number,
    sort: string,
    order: 'asc' | 'desc',
    q?: string,
    range: CostDateRange = {}
  ) => {
    const params = new URLSearchParams({
      _start: String(start),
      _end: String(end),
      _sort: sort,
      _order: order.toUpperCase(),
    });
    if (q) params.set('q', q);
    for (const [key, value] of Object.entries(range)) if (value) params.set(key, value);

    return apiClient.get<{
      data: Array<{
        userId: string;
        email: string;
        name: string | null;
        totalCost: number;
        totalTokens: number;
        queryCount: number;
        costCoverage?: AICostCoverage;
      }>;
      total: number;
    }>(`/admin/ai-costs?${params.toString()}`);
  },

  getAICostCalls: async (offset: number, limit: number, filters: AICostCallFilters = {}) => {
    const params = new URLSearchParams({ offset: String(offset), limit: String(limit) });
    for (const [key, value] of Object.entries(filters)) if (value) params.set(key, value);
    return apiClient.get<AICostCallsResult>(`/admin/ai-costs/calls?${params.toString()}`);
  },

  /**
   * Get global AI cost statistics
   */
  getAICostStats: async () => {
    return apiClient.get<{
      totalCostAllTime: number;
      totalCostThisMonth: number;
      totalCostToday: number;
      totalQueries: number;
      totalTokens: number;
      uniqueUsers: number;
      costCoverage?: { allTime: AICostCoverage; thisMonth: AICostCoverage; today: AICostCoverage };
      costByModel: Array<{
        model: string;
        totalCost: number;
        queryCount: number;
        totalTokens: number;
      }>;
      dailyCosts: Array<{
        date: string;
        cost: number;
        queries: number;
      }>;
    }>('/admin/ai-costs/stats');
  },

  /**
   * Get detailed AI cost breakdown for a specific user
   */
  getAICostUserDetail: async (userId: string, range: CostDateRange = {}) => {
    const period = new URLSearchParams();
    for (const [key, value] of Object.entries(range)) if (value) period.set(key, value);
    return apiClient.get<{
      userId: string;
      email: string;
      name: string | null;
      totalCost: number;
      monthlyCost: number;
      costCoverage?: { allTime: AICostCoverage; thisMonth: AICostCoverage };
      costByModel: Array<{
        model: string;
        totalCost: number;
        queryCount: number;
      }>;
      recentLogs: Array<{
        id: string;
        model: string;
        inputTokens: number;
        outputTokens: number;
        totalTokens: number;
        costUSD: number;
        costSource?: 'provider' | 'catalog_estimate' | 'unknown' | null;
        usageReported?: boolean | null;
        timestamp: string;
      }>;
    }>(`/admin/ai-costs/users/${encodeURIComponent(userId)}?${period}`);
  },

  /**
   * Create or update a card translation
   */
  upsertCardTranslation: async (cardId: string, language: string, statement: string) => {
    return apiClient.post<{ language: string; statement: string }>(
      `/admin/content/cards/${cardId}/translations`,
      { language, statement }
    );
  },

  /**
   * Delete a card translation
   */
  deleteCardTranslation: async (cardId: string, language: string) => {
    return apiClient.delete<{ success: boolean }>(
      `/admin/content/cards/${cardId}/translations/${language}`
    );
  },

  /**
   * Create or update a question translation
   */
  upsertQuestionTranslation: async (questionId: string, language: string, text: string, options: string[]) => {
    return apiClient.post<{ language: string; text: string; options: string[] }>(
      `/admin/content/questions/${questionId}/translations`,
      { language, text, options }
    );
  },

  /**
   * Delete a question translation
   */
  deleteQuestionTranslation: async (questionId: string, language: string) => {
    return apiClient.delete<{ success: boolean }>(
      `/admin/content/questions/${questionId}/translations/${language}`
    );
  },




  /**
   * Trigger questionnaire wave notifications
   * @param userIds - Optional array of user IDs. If empty, triggers for all eligible users.
   */
  triggerQuestionnaireWave: async (userIds?: string[]) => {
    return apiClient.post<{
      success: boolean;
      sentCount: number;
    }>('/admin/notifications/trigger-questionnaire-wave', { userIds });
  },




  /**
   * Get all notification translations
   */
  getNotificationTranslations: async () => {
    return apiClient.get<{
      data: Array<{
        type: string;
        variables: string[];
        translations: Array<{
          language: string;
          titleTemplate: string;
          messageTemplate: string;
        }>;
      }>;
      total: number;
    }>('/admin/notification-translations');
  },

  /**
   * Get translations for a specific notification type
   */
  getNotificationTranslationType: async (type: string) => {
    return apiClient.get<{
      type: string;
      variables: string[];
      translations: Array<{
        language: string;
        titleTemplate: string;
        messageTemplate: string;
      }>;
    }>(`/admin/notification-translations/${type}`);
  },

  /**
   * Create or update a notification translation
   */
  upsertNotificationTranslation: async (
    type: string,
    language: string,
    titleTemplate: string,
    messageTemplate: string
  ) => {
    return apiClient.post<{
      type: string;
      variables: string[];
      translations: Array<{
        language: string;
        titleTemplate: string;
        messageTemplate: string;
      }>;
    }>(`/admin/notification-translations/${type}/${language}`, {
      titleTemplate,
      messageTemplate,
    });
  },

  /**
   * Delete a notification translation
   */
  deleteNotificationTranslation: async (type: string, language: string) => {
    return apiClient.delete<{ success: boolean }>(
      `/admin/notification-translations/${type}/${language}`
    );
  },

  /**
   * Get tool usage statistics
   */
  getToolStats: async () => {
    return apiClient.get<{
      summary: {
        totalCalls: number;
        successRate: number;
        avgDurationMs: number;
        last24h: number;
        last7d: number;
        last30d: number;
      };
      byTool: Array<{
        toolName: string;
        totalCalls: number;
        successRate: number;
        avgDurationMs: number;
      }>;
    }>('/admin/tool-stats');
  },
};
